import {CrudAccessGroup, CrudObject, Graphql} from '@hc/graphql'
import {reactive, toRefs} from 'vue'
import {FetchEventHandler, ListFetch, ListFetchIncompleteError} from "@hc/graphql"

export interface UseCrudListParams {
  collection: string
  access: CrudAccessGroup
  filter?: object
  fetchEventHandler?: FetchEventHandler
}

export interface UseCrudRelationListParams extends UseCrudListParams {
  relation: string
  parentId: number | string
}

export type CrudListError =
  // count or first page failed, nothing loaded
  | {type: 'failed', error: unknown}
  // a later page failed, list only partially loaded
  | {type: 'incomplete', error: unknown, loaded: number, total: number}

// human readable message for a list load error
export function crudListErrorMessage(error: CrudListError, title: string): string {
  if(error.type == 'incomplete'){
    return `List incomplete: loaded ${error.loaded} of ${error.total} ${title}`
  }
  const detail = error.error instanceof Error ? error.error.message : String(error.error)
  return `Failed to load ${title}: ${detail}`
}


export function useCrudList(params: UseCrudListParams){

  const collection = Graphql.collection(params.collection)
  const staticFilter = params.filter || null
  const fetch = new ListFetch(collection.name, true)

  const state = reactive({
    // true until the first page is loaded
    loading: false,
    // true until all pages are loaded
    fetching: false,
    count: null as number | null,
    list: [] as Array<CrudObject>,
    error: null as CrudListError | null,
  })
  // incremented on every load(), used so only the latest load writes state
  let loadId = 0

  const buildFilter = (customFilter: Array<object> | null) => {
    const filterItems = []
    if(staticFilter) filterItems.push(staticFilter)
    if(customFilter) filterItems.push(...customFilter)
    if(filterItems.length == 1) return filterItems[0]
    if(filterItems.length > 1) return {_and: filterItems}
    return null
  }

  const actions = {
    // resolves when all pages are loaded, never rejects (see state.error)
    async load(customFilter?: Array<object> | null){
      const filter = buildFilter(customFilter)
      const currentLoad = ++loadId
      const isCurrent = () => currentLoad === loadId

      state.loading = true
      state.fetching = true
      state.error = null
      try {
        const result = await fetch.fetch({filter}, params.fetchEventHandler)
        // a newer load started meanwhile: drop this outdated result
        if(!isCurrent()) return
        state.count = result.totalCount
        state.list = result.items
        state.loading = false
        await result.done
      } catch(e) {
        if(!isCurrent()) return
        console.error(`useCrudList(${collection.name}): load failed`, e)
        state.error = e instanceof ListFetchIncompleteError
          ? {type: 'incomplete', error: e.original, loaded: e.loaded, total: e.totalCount}
          : {type: 'failed', error: e}
      } finally {
        if(isCurrent()){
          state.loading = false
          state.fetching = false
        }
      }
    },
    // manual refresh: also reload cached join objects
    async refresh(customFilter?: Array<object> | null){
      fetch.clearJoinCache()
      await actions.load(customFilter)
    },
    deleteItems(items: Array<{id: CrudObject['id']}>){
      for(const remove of items){
        const index = state.list.findIndex((i) => i.id == remove.id)
        if(index >= 0) state.list.splice(index, 1)
      }
    },
    addItems(items: Array<CrudObject>){
      const existingIds = new Set(state.list.map((i) => i.id))
      for(const item of items){
        if(existingIds.has(item.id)) continue
        existingIds.add(item.id)
        state.list.push(item)
      }
    }
  }

  return {
    ...toRefs(state),
    collection,
    ...actions
  }
}

export function useCrudRelationList(params: UseCrudRelationListParams){
  const parentCollection = Graphql.collection(params.collection)
  if(!parentCollection) throw new Error("Invalid crud relation:" + params.collection + '/' + params.relation)
  const relationCollection = parentCollection.relation(params.relation)
  const relationFilter = {...params.filter}
  relationFilter[parentCollection.relationKey(params.relation)] = {_eq: params.parentId}
  return useCrudList({
    collection: relationCollection.name,
    filter: relationFilter,
    access: params.access,
    fetchEventHandler: params.fetchEventHandler,
  })
}
